import { Link, usePage } from "@inertiajs/react"
import { type PropsWithChildren, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { UnverifiedEmailBanner } from "@/components/email-verification/unverified-email-banner"
import Heading from "@/components/heading"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { useCurrentContext } from "@/hooks/use-current-context"
import { cn } from "@/lib/utils"
import {
  settingsAppearancePath,
  settingsEmailPath,
  settingsMembershipsPath,
  settingsNotificationsPath,
  settingsPasswordPath,
  settingsProfilePath,
  settingsSessionsPath,
  settingsSocialAccountsPath,
} from "@/routes"
import type { NavItem } from "@/types"

export default function SettingsLayout({ children }: PropsWithChildren) {
  const { t } = useTranslation()
  const { url } = usePage()
  const { currentUser } = useCurrentContext()
  const isCreator = currentUser?.role === "creator"

  const sidebarNavItems: NavItem[] = useMemo(
    () => [
      {
        title: t("settings.layout.nav.profile"),
        href: settingsProfilePath(),
        icon: null,
      },
      // OKS-40 — Creator-only sub-pages (Notifications + Memberships).
      // Hidden for admin / brand / agency users — they have no
      // email-event surface or crew-membership relationships.
      ...(isCreator
        ? [
            {
              title: t("settings.layout.nav.notifications"),
              href: settingsNotificationsPath(),
              icon: null,
              "data-testid": "settings-nav-notifications",
            },
            {
              title: t("settings.layout.nav.memberships"),
              href: settingsMembershipsPath(),
              icon: null,
              "data-testid": "settings-nav-memberships",
            },
          ]
        : []),
      {
        title: t("settings.layout.nav.email"),
        href: settingsEmailPath(),
        icon: null,
      },
      {
        title: t("settings.layout.nav.password"),
        href: settingsPasswordPath(),
        icon: null,
      },
      {
        title: t("settings.layout.nav.sessions"),
        href: settingsSessionsPath(),
        icon: null,
      },
      ...(isCreator
        ? [
            {
              // OKS-23 — Social accounts (TikTok + future IG/FB) —
              // creator-only since OAuth links are creator-scoped.
              title: t("settings.layout.nav.social_accounts"),
              href: settingsSocialAccountsPath(),
              icon: null,
              "data-testid": "settings-nav-social-accounts",
            },
          ]
        : []),
      {
        title: t("settings.layout.nav.appearance"),
        href: settingsAppearancePath(),
        icon: null,
      },
    ],
    [t, isCreator],
  )

  return (
    <div className="px-4 py-6">
      <Heading
        title={t("settings.layout.heading")}
        description={t("settings.layout.description")}
        data-testid="settings-heading"
      />

      {/* OKS-799 — the verification prompt renders ONCE for the whole
          Settings surface, here in the shared frame: below the page heading
          and above the nav+content row, so it reads as "about this account"
          rather than "about this tab" (UX Spec §2). Mounting it here rather
          than on Settings → Email is deliberate — the account menu lands a
          creator on Settings → Profile, and a prompt only on the Email tab
          would be invisible on the tab they actually arrive at (PRD OQ1).

          This layout is SHARED with brand / agency / admin users. There is
          deliberately NO role conditional here: the component's own
          `role === "creator"` early return is the single gate. That means
          it is also the single POINT OF FAILURE — if it is ever removed,
          brand admins start seeing a prompt about a creator account and
          nothing else catches it. Do not "simplify" on the assumption that
          this layout guards too. Locked by spec.

          `mb-8` reproduces the gap `<Heading>`'s own `mb-8` provides, so
          the nav row keeps its spacing whether or not the prompt renders. */}
      <UnverifiedEmailBanner className="mb-8" />

      <div
        data-testid="settings-nav-content-row"
        className="flex flex-col space-y-8 lg:flex-row lg:space-y-0 lg:space-x-12"
      >
        <aside className="w-full max-w-xl lg:w-48">
          <nav className="flex flex-col space-y-1 space-x-0">
            {sidebarNavItems.map((item, index) => (
              <Button
                key={`${item.href}-${index}`}
                size="sm"
                variant="ghost"
                asChild
                className={cn("w-full justify-start", {
                  "bg-muted": url === item.href,
                })}
              >
                <Link href={item.href}>
                  {item.icon && <item.icon className="h-4 w-4" />}
                  {item.title}
                </Link>
              </Button>
            ))}
          </nav>
        </aside>

        <Separator className="my-6 md:hidden" />

        <div className="flex-1 md:max-w-2xl">
          <section className="max-w-xl space-y-12">{children}</section>
        </div>
      </div>
    </div>
  )
}
